import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../lib/store.ts';
import { FAIR_USE_EVENT, PAYWALL_EVENT, type PaywallReason } from '../lib/plan.ts';

/**
 * Opens when the API answers 402 (see ``lib/plan.ts``) or the user taps a
 * "Get Pro" link. Pro is bought in the Soigneur app for now (Stripe on the
 * web comes later), so the dialog explains Pro and points there.
 *
 * Also turns a 429 ``fair_use`` into a toast.
 */
export function PaywallDialog() {
  const { t } = useTranslation();
  const [reason, setReason] = useState<PaywallReason | null>(null);
  const freeMonthly = useStore((s) => s.user?.free_chat_monthly ?? 5);
  const freeUploads = useStore((s) => s.user?.free_uploads_monthly ?? 10);

  useEffect(() => {
    const onPaywall = (e: Event) => setReason((e as CustomEvent<PaywallReason>).detail ?? 'pro_feature');
    // Pushed straight into the store: addToast skips error toasts right after
    // a gate response, and this one is the message for it.
    const onFairUse = () =>
      useStore.setState((s) => ({
        toasts: [...s.toasts, { id: `fair-use-${Date.now()}`, message: t('plan.fairUse'), type: 'error' }],
      }));
    globalThis.addEventListener(PAYWALL_EVENT, onPaywall);
    globalThis.addEventListener(FAIR_USE_EVENT, onFairUse);
    return () => {
      globalThis.removeEventListener(PAYWALL_EVENT, onPaywall);
      globalThis.removeEventListener(FAIR_USE_EVENT, onFairUse);
    };
  }, [t]);

  useEffect(() => {
    if (!reason) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setReason(null);
    };
    globalThis.addEventListener('keydown', onKey);
    return () => globalThis.removeEventListener('keydown', onKey);
  }, [reason]);

  if (!reason) return null;

  const titles: Record<PaywallReason, string> = {
    free_quota: t('plan.paywall.titleQuota', { count: freeMonthly }),
    upload_quota: t('plan.paywall.titleUploads', { count: freeUploads }),
    pro_feature: t('plan.paywall.title'),
    history_import: t('plan.paywall.titleHistory'),
  };
  const title = titles[reason];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <dialog
        open
        aria-modal="true"
        aria-labelledby="paywall-title"
        className="static m-0 w-full max-w-md rounded-2xl border border-(--color-border) bg-(--color-bg-secondary) text-(--color-text-primary) p-6 flex flex-col gap-5"
      >
        <div className="flex flex-col gap-2">
          <span className="self-start rounded px-2 py-0.5 text-xs font-bold tracking-wider bg-amber-400/15 text-amber-300">
            PRO
          </span>
          <h2 id="paywall-title" className="text-xl font-bold">{title}</h2>
          <p className="text-sm text-(--color-text-secondary)">{t('plan.paywall.subtitle')}</p>
        </div>
        <ul className="flex flex-col gap-3 text-sm">
          {(['chat', 'summaries', 'plans'] as const).map((key) => (
            <li key={key} className="flex gap-3">
              <span aria-hidden="true" className="text-(--color-accent)">✓</span>
              <span>{t(`plan.paywall.benefits.${key}`)}</span>
            </li>
          ))}
        </ul>
        <p className="text-sm rounded-xl bg-(--color-bg-tertiary) px-4 py-3">
          {t('plan.paywall.getInApp')}
        </p>
        <button
          type="button"
          onClick={() => setReason(null)}
          className="bg-green-400 text-gray-900 px-5 py-3 rounded-xl font-semibold text-sm hover:bg-green-300 transition-colors"
        >
          {t('plan.paywall.close')}
        </button>
      </dialog>
    </div>
  );
}
